import { useEffect, useRef } from 'react';
import { css } from '@emotion/css';
import { useApplicationData } from '@provider';
import { Box } from '@spark-web/box';
import { Divider } from '@spark-web/divider';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';
import { TextList } from '@spark-web/text-list';
import { LOAN_CONTRACT, productIds } from '@utils';
import LoanContractItem from './LoanContractItem';
import LoanContractTermsHESL from './LoanContractTermsHESL';
import LoanScheduleTable from './LoanScheduleTable';

type LoanContractProps = {
  setReview: (value: boolean) => void;
};

function LoanContract({ setReview }: LoanContractProps) {
  const { TITLE, MESSAGE } = LOAN_CONTRACT;
  const { quote, application } = useApplicationData(['quote', 'application']);

  const isHESL = quote?.financialProduct?.id === productIds.BHESL;
  const ref = useRef<HTMLDivElement | null>(null);

  const productConfiguration = application?.productConfiguration;

  useEffect(() => {
    // enable accept button when the contract is reviewed (scrolled to the end)
    const observer = new IntersectionObserver(([entry]) => setReview(entry.isIntersecting));
    if (ref.current) {
      observer.observe(ref?.current);
    }

    return () => {
      if (ref.current) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <Stack
      gap="xxlarge"
      className={css({
        height: '550px',
        overflowY: 'scroll',
      })}
      border="standard"
      background="surfaceMuted"
    >
      <Box paddingX="medium" paddingY="medium">
        <Text tone="placeholder">
          <Stack gap="xlarge" paddingY="large" marginBottom="xlarge">
            <Text weight="semibold">{TITLE}</Text>
            <Text size="small">
              <Stack gap="xlarge">{MESSAGE(quote?.vendor?.tradingName ?? '')}</Stack>
            </Text>
            <LoanScheduleTable />
          </Stack>
          <Stack gap="large" paddingBottom="medium">
            <Text size="xsmall">
              <Stack gap="large">
                <Text>*This amount may be less if you make extra payments.</Text>
                <Text>
                  If you have any questions about this Schedule or anything relating to your Loan, please contact us as
                  soon as possible.
                </Text>
                <Text>Thank you for choosing Brighte.</Text>
                <Text weight="semibold">Kind Regards,</Text>
                <Text weight="semibold">Brighte Customer Care Team</Text>
              </Stack>
            </Text>
          </Stack>
          <Stack gap="xlarge" paddingTop="xlarge">
            <Divider />
            <Text weight="semibold">{quote?.financialProduct?.name} Contract</Text>
            {isHESL ? (
              <LoanContractTermsHESL />
            ) : (
              <>
                <LoanContractItem
                  title="1. Your contract with us"
                  terms={[
                    {
                      termTitle: '1.1. Contract Terms',
                      message: `Please read these Terms and Conditions and the Schedule that includes your loan details. Together
                they make up your loan contract and include the information we must provide you. Your loan contract
                is formed by your accepting our loan offer in the manner provided for within the terms of our loan
                offer.`,
                    },
                    {
                      termTitle: '1.2. The name of your loan',
                      message: `The form of this loan contract is used for both Brighte's Personal Loan and Brighte's Green Loan.
                For the purposes of this loan contract, where the word Personal is used in the context 'Brighte
                Personal Loan', it can also be read as 'Green' in the context of 'Brighte Green Loan'.`,
                    },
                  ]}
                />
                <LoanContractItem
                  title="2. Setting up your loan"
                  terms={[
                    {
                      termTitle: '2.1. Conditions of funding your loan',
                      message:
                        'Before we fund your loan, you must meet our requirements including completing an authority form to nominate an account we can debit repayments from.',
                    },
                    {
                      termTitle: '2.2. How we fund your loan',
                      message: (
                        <>
                          <Text>
                            Your loan will be used only for the purposes you indicated to us. This means that:
                          </Text>
                          <Box marginLeft="xlarge">
                            <TextList gap="small">
                              <Text>
                                the loan amount will be used to finance your purchase of goods and services from our
                                approved vendor;
                              </Text>
                              <Text>
                                the loan amount will be paid on your behalf to the vendor named in the Schedule; advance
                              </Text>
                              <Text>
                                we will pay the vendor the loan amount on your behalf, on the next business day after we
                                are informed by the vendor that the purchased goods and services have been provided,
                                delivered and/or installed (as required under your contract with the vendor), or on such
                                following business day as we reasonably agree with the vendor.
                              </Text>
                            </TextList>
                          </Box>
                        </>
                      ),
                    },
                  ]}
                />
                <LoanContractItem
                  title="3. Managing your loan"
                  terms={[
                    {
                      termTitle: '3.1. Transactions on your loan account',
                      message: (
                        <>
                          Once you become bound by your loan contract with us, we will open a loan account in your name.
                          We will use your loan account to debit and credit amounts owing and paid by you under your
                          loan contract. For example:
                          <Box marginLeft="xlarge">
                            <TextList gap="small">
                              <Text>
                                when we make a payment to a vendor on your behalf, we will debit the amount of that
                                advance to your loan account; and
                              </Text>
                              <Text>
                                when we deduct from the loan amount any fees that are payable to us, we will debit the
                                amount deducted to your loan account.
                              </Text>
                            </TextList>
                          </Box>
                        </>
                      ),
                    },
                    {
                      termTitle: '3.2. Repaying your loan',
                      message: (
                        <>
                          Your first repayment will become due and payable to us{' '}
                          {productConfiguration?.firstPaymentDelayDays} days after the day on which we are informed by
                          the vendor that the purchased goods and services have been provided, delivered and/or
                          installed (as required under your contract with the vendor), or on such earlier date that you
                          nominate to us as the earlier day for your first repayment, which day must fall within the
                          first {productConfiguration?.firstPaymentDelayDays} days after we first make any advance of
                          the loan amount to the vendor. When you nominate an earlier day for the first repayment, you
                          must do so at least two business days before the nominated date. We will tell you by email or
                          by SMS when we have been informed by the vendor that the purchased goods and services have
                          been provided, delivered and/or installed (as required under your contract with the vendor).
                          <Text>
                            Your subsequent repayment dates will occur after your first repayment became due and payable
                            and at the periodic intervals stated in the Schedule. However:
                          </Text>
                          <Box marginLeft="xlarge">
                            <TextList gap="small">
                              <Text>
                                if the day for a repayment is not a business day, the required repayment is payable to
                                us on the next business day. A business day is day on which banks are open for business
                                in New South Wales; and
                              </Text>
                              <Text>
                                if the day for a monthly repayment (where applicable) falls on a day of a month that
                                does not occur particular month for the applicable repayment, the repayment will be
                                payable on the last day of that month.
                              </Text>
                            </TextList>
                          </Box>
                          <Text>The Schedule sets out the required repayment amounts.</Text>
                          <Text>
                            You must continue to make the required repayments specified in the Schedule until the
                            outstanding balance of your loan account, and any accrued but undebited interest has been
                            paid in full. In the event that the total of the remaining outstanding balance and any
                            accrued interest that has not yet been debited to your loan account is less than the amount
                            of the required repayment amount, then you are only required to pay the remaining unpaid
                            outstanding balance, together with the amount of the accrued but undebited interest and any
                            other fees that are then payable in order to discharge your payment obligations under your
                            loan contract.
                          </Text>
                          <Text>
                            Until your loan is repaid in full, together with all interest and fees payable, you will
                            need to continue making regular repayments, even if you’ve made extra repayments (see 3.3).
                          </Text>
                          <Text>
                            We have calculated the repayment amounts on the assumption that the whole of the loan amount
                            is advanced on the date set out in the Schedule, and on the assumptions that your first
                            repayment will be due {productConfiguration?.firstPaymentDelayDays} days after that date and
                            that all repayments will be made on time and will be due and payable on a business day. The
                            actual total amount that will become payable by you under this loan contract will depend on
                            things like when you make the repayments.
                          </Text>
                          <Text>
                            We may apply your payments to any amount you owe us and in any order we choose, including
                            towards the payment of any outstanding fees before your payment is applied towards an
                            outstanding repayment. We will give you credit for any payment on a day that is not later
                            than the day that we receive the benefit of that payment. If a repayment is dishonoured, we
                            treat it as if it was never made. Each payment that you make to us that is not made under a
                            direct debit request or other recurring payment authority must be accompanied by details of
                            your loan with us.
                          </Text>
                          <Text>
                            If you wish to cancel or alter your direct debit request in our favour, please call us on
                            1300 BRIGHTE. At all times during the term of this loan contract, you must ensure that we
                            hold a valid direct debit request in our favour that authorises us to collect or recover
                            from your nominated account with your financial institution all amounts that are payable to
                            us from time to time under this loan contract.
                          </Text>
                          <Text>
                            Subject to any statutory right of set off that you may have and that cannot be excluded by
                            agreement, you must pay all amounts you owe us without making any set-off or deducting any
                            amounts you believe we owe you.
                          </Text>
                        </>
                      ),
                    },
                    {
                      termTitle: '3.3. Paying your loan off early',
                      message: 'You may pay off your loan early without any penalty or early termination fees.',
                    },
                    {
                      termTitle: '3.4. Notices',
                      message: (
                        <>
                          Any communication, notice or other document to be given or served under or in connection with
                          your loan contract may be:
                          <Box marginLeft="xlarge">
                            <TextList gap="small">
                              <Text>delivered personally to you;</Text>
                              <Text>posted to or left at your residential or business address last known to us;</Text>
                              <Text>sent electronically, including by email or SMS; or;</Text>
                              <Text>given in any other way permitted by law.</Text>
                            </TextList>
                          </Box>
                          The notice may be signed by any employee, solicitor, or agent on our behalf. A notice is taken
                          to have been given at the following times:
                          <Box marginLeft="xlarge">
                            <TextList gap="small">
                              <Text>
                                notices delivered personally or left at your address will be treated as given at the
                                time of delivery;
                              </Text>
                              <Text>
                                notices sent by pre-paid post will be treated as given in the ordinary course of post,
                                but no later fifth working day after posting, even if not received for any reason; and
                              </Text>
                              <Text>
                                notices sent electronically or by fax will be treated as given when the machine or
                                system from which they were sent indicates that they have been successfully transmitted
                                or sent.
                              </Text>
                            </TextList>
                          </Box>
                          If the law does require us to give you notice before exercising rights, we may not have to
                          give notice if:
                          <Box marginLeft="xlarge">
                            <TextList gap="small">
                              <Text>we cannot locate you after making reasonable attempts to do so; or</Text>
                              <Text>you are insolvent; or</Text>
                              <Text>we are authorised by a court.</Text>
                            </TextList>
                          </Box>
                        </>
                      ),
                    },
                  ]}
                />
                <LoanContractItem
                  title="4. Fees and interest"
                  terms={[
                    {
                      termTitle: '4.1. Fees you may have to pay',
                      message:
                        'The fees you need to pay are explained in the Schedule and will be debited to your loan account when they become payable.',
                    },
                    {
                      termTitle: '4.2. Fixed interest rate',
                      message: 'Your fixed annual percentage rate is set out in the Schedule.',
                    },
                    {
                      termTitle: '4.3. How we calculate interest',
                      message: (
                        <Stack gap="xlarge">
                          <Text>
                            Interest is calculated daily on the unpaid balance of your loan account at the end of the
                            day using the daily percentage rate (the annual percentage rate divided by 365).
                          </Text>
                          <Text>
                            We debit accrued interest to your loan account at periodic intervals commencing after the
                            first advance of any part of the loan amount.
                          </Text>
                          <Text>
                            We will first debit accrued interest to your loan account on the day your first repayment is
                            payable to us, or if that day is not a business day, on the next business day.
                          </Text>
                          <Text>
                            We will subsequently debit accrued interest, that has not been previously debited to your
                            loan account, on the days that each of your subsequent repayments are payable to us (as
                            provided for in the Schedule and clause 3) or if the relevant day is not a business day, on
                            the next business day.
                          </Text>
                          <Text>
                            After the scheduled repayment dates have passed, and if your loan account has not been
                            repaid in full, we will continue to debit accrued interest to your loan account at the same
                            periodic intervals that applied to your repayments.
                          </Text>
                          <Text>
                            If your loan is repaid in full between these periodic intervals, we will also debit any
                            accrued but undebited interest to your loan account immediately before your loan is repaid
                            in full.
                          </Text>
                        </Stack>
                      ),
                    },
                  ]}
                />
                <LoanContractItem
                  title="5. Default and other matters"
                  terms={[
                    {
                      termTitle: '5.1. When you’re in default',
                      message:
                        'You’re in default if you don’t make your required repayments on time or don’t comply with these Terms and Conditions.',
                    },
                    {
                      termTitle: '5.2. Actions we may take',
                      message: (
                        <Stack gap="xlarge">
                          <Text>
                            If you are in default, we may send you a notice requiring you to fix the default within the
                            stated time (at least 30 days). You may not always get a notice – for example if we have
                            made reasonable attempts to locate you without success.
                          </Text>
                          <Text>
                            If you are in default because of a failure to make a required payment to us, we may require
                            you to repay the whole of the outstanding balance of your loan account and any additional
                            accrued interest and fees, if you do not rectify the default within the time stated in the
                            notice of your default. Where we have elected to impose this acceleration requirement in the
                            event of your continuing default, we will tell you this in our notice to you of your default
                            as well as the amount required to pay out your loan contract (as accelerated).
                          </Text>
                          <Text>
                            If you don’t fix the default within the time allowed or we are not required to give you a
                            notice, then we may take legal action to recover any amounts you owe us, including any
                            accelerated liabilities in consequence of your failure to rectify a payment default within
                            the required time.
                          </Text>
                          <Text>
                            We’ll debit your loan account with any expenses we reasonable incur in enforcing these
                            rights, and we may do so at any time after we have incurred the relevant expense.
                          </Text>
                        </Stack>
                      ),
                    },
                    {
                      termTitle: '5.3. If we take legal action and obtain judgment',
                      message: (
                        <Stack gap="xlarge">
                          <Text>
                            You must pay interest on the judgment amount at the higher of the rate set out in the court
                            order or the rate we may charge under the contract.
                          </Text>
                          <Text>Interest is otherwise calculated as set out in 4.3.</Text>
                        </Stack>
                      ),
                    },
                    {
                      termTitle: '5.4. Let us know of any changes',
                      message:
                        'You must tell us as soon as possible if you change your name, address, other contact details and situation changes.',
                    },
                    {
                      termTitle: '5.5. Vendor Fee',
                      message: `If you have been referred to us by a vendor, then we may pay that vendor a referral fee for referring you
                to us. That fee, where payable, will be fixed and not dependent on your loan amount. Where that fee will
                be payable, the amount of the fee will be set out in the Schedule`,
                    },
                    {
                      termTitle: '5.6. Privacy',
                      message: (
                        <Text>
                          Your privacy is important to us. For details of when and how we collect, use and disclose your
                          information and of rights you have (for example, to choose how you want us to communicate with
                          us), see our Privacy Policy at{' '}
                          <TextLink href="https://brighte.com.au/privacy" target="_blank">
                            https://brighte.com.au/privacy
                          </TextLink>{' '}
                          or contact us on 1300-BRIGHTE.
                        </Text>
                      ),
                    },
                    {
                      termTitle: '5.7. Energy Data',
                      message: ` At Brighte, we value innovation and strive to understand our customers’ needs to provide the best service.
                To facilitate this, you agree that we may request access to current and historical metering data from your
                electricity provider on your behalf. You will provide us with any further consents or additional details
                we request to allow us to access this data (including account number, national metering identifier and
                meter number). Metering data will be collected, used and disclosed in accordance with our Privacy Policy.`,
                    },
                  ]}
                />
                <LoanContractItem title="6. Changes to your contract">
                  <>
                    We can:
                    <Text>
                      <Text weight="semibold" inline={true}>
                        6.1.
                      </Text>{' '}
                      change your repayments if we consider this to be necessary so that you repay your loan within the
                      loan term provided for in the Schedule;
                    </Text>
                    <Text>
                      <Text weight="semibold" inline={true}>
                        6.2.
                      </Text>{' '}
                      change the amount of or waive any fee as long as you are no worse off by us doing so.
                    </Text>
                    <Text>
                      We will act reasonably when exercising these rights and only do so for legitimate business
                      purposes.
                    </Text>
                    <Text>We will tell of these changes by giving you 30 days’ notice in writing.</Text>
                  </>
                </LoanContractItem>
                <LoanContractItem title="7. Assignment">
                  <Text>
                    We may assign or transfer our rights under this loan contract without your consent. You cannot
                    assign your rights under this loan contract without our consent.,
                  </Text>
                </LoanContractItem>
                <LoanContractItem title="8. Electronic consents">
                  <>
                    You consent to:
                    <Box marginLeft="xlarge">
                      <TextList gap="small">
                        <Text>
                          the execution of this loan contract and the loan contract offer by Brighte Capital Pty Limited
                          by means of an electronic signature and in the manner provided for in this loan contract and
                          in the loan contract offer; and
                        </Text>
                        <Text>
                          the documentation and provision to you of the loan contract (including the offer of the loan
                          contract) in electronic form.
                        </Text>
                      </TextList>
                    </Box>
                    You acknowledge that you have already been advised by us that where your consent is given to the
                    matters set out in this clause 8:
                    <Box marginLeft="xlarge">
                      <TextList gap="small">
                        <Text>paper documents may no longer be given; and</Text>
                        <Text>
                          electronic communications must be regularly checked by you for electronic documents; and
                        </Text>
                        <Text>
                          any consent to the giving of documents by electronic communication may be withdrawn at any
                          time.
                        </Text>
                      </TextList>
                    </Box>
                  </>
                </LoanContractItem>
              </>
            )}
          </Stack>
          <Stack gap="xlarge" paddingTop="xlarge">
            <Text size="xsmall">
              <Text weight="semibold" inline={true}>
                SIGNED
              </Text>{' '}
              for{' '}
              <Text weight="semibold" inline={true}>
                Brighte Capital Pty Limited
              </Text>{' '}
              by its duly authorised officer
            </Text>
            <Box ref={ref}>
              {/* TODO: signature may change, still under discussion */}
              <img src="./img/gn-signature.png" width="150" height="30" alt="contract signature" />
              <Text size="xsmall">Signed by: Greg Neilsen</Text>
            </Box>
          </Stack>
        </Text>
      </Box>
    </Stack>
  );
}

export default LoanContract;
